import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { trpc } from "../../lib/trpc";

type Severity = "low" | "medium" | "high" | "critical";
const SEV_LABELS: Record<Severity, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const SEV_OPTS = Object.entries(SEV_LABELS) as [Severity, string][];
const STATUS_LABELS: Record<string, string> = { open: "Açık", resolved: "Çözüldü", converted: "Bakıma dönüştü", cancelled: "İptal" };
const TYPE_LABELS: Record<string, string> = { uretim_hatti: "Hat", unite: "Ünite", makina: "Makina", uretim_merkezi: "Merkez" };

interface Props {
  open: boolean;
  centerId: string;
  centerName: string;
  onClose: () => void;
}

interface LocRow { id: string; name: string; type: string; parentId: string | null; }

// datetime-local için: Date → "YYYY-MM-DDTHH:mm" (yerel saat).
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
const intOrNull = (v: string): number | null => { const t = v.trim(); if (!t) return null; const n = Math.round(Number(t.replace(",", "."))); return Number.isFinite(n) ? n : null; };

/**
 * Üretim hattı arıza bildirimi: ekipman ağacından ünite/makina seç + merkez kataloğundan
 * aranabilir arıza tipi + üretim durdu mu / süre / tahmini + açıklama. Son bildirimler listelenir.
 */
export function FaultReportDrawer({ open, centerId, centerName, onClose }: Props) {
  const qc = useQueryClient();
  const locsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 30_000 });
  const catQ = trpc.faultCatalog.list.useQuery({ centerId, activeOnly: true }, { enabled: open && !!centerId, staleTime: 10_000 });
  const reportsQ = trpc.faultReport.list.useQuery({ centerId, pageSize: 15 }, { enabled: open && !!centerId, staleTime: 5_000 });
  const createMut = trpc.faultReport.create.useMutation();
  const resolveMut = trpc.faultReport.resolve.useMutation({ onSuccess: () => qc.invalidateQueries() });
  // Bakım emrine dönüştürme — yalnız manage:maintenance izni olan kullanıcıya görünür (tıkla-patla olmasın).
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const canMaint = !!meQ.data?.user && (meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).includes("manage:maintenance"));
  const convertMut = trpc.maintenance.order.create.useMutation();
  const [convertTarget, setConvertTarget] = useState<{ id: string; label: string; locationId: string | null; severity: string | null } | null>(null);
  const [convertedCode, setConvertedCode] = useState<string | null>(null);

  const [locationId, setLocationId] = useState<string | null>(null);
  const [eqQuery, setEqQuery] = useState("");
  const [expandedEq, setExpandedEq] = useState<Set<string>>(new Set()); // varsayılan: hepsi KAPALI (yalnız hat seviyesi görünür)
  const [catalogId, setCatalogId] = useState<string | null>(null);
  const [productionStopped, setProductionStopped] = useState(false);
  const [startedAt, setStartedAt] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [estMin, setEstMin] = useState("");
  const [severity, setSeverity] = useState<Severity | "">("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState<string | null>(null);

  function resetForm() {
    setLocationId(null); setEqQuery(""); setExpandedEq(new Set()); setCatalogId(null); setProductionStopped(false);
    setStartedAt(toLocalInput(new Date())); setDurationMin(""); setEstMin(""); setSeverity(""); setDescription(""); setErr(null);
  }
  useEffect(() => { if (open) resetForm(); /* eslint-disable-next-line */ }, [open, centerId]);

  // Merkezin altındaki ekipman ağacı (hat → ünite → makina) — düz listeden DFS (pre-order).
  const equipment = useMemo(() => {
    const rows = (locsQ.data ?? []) as unknown as LocRow[];
    const byParent = new Map<string, LocRow[]>();
    rows.forEach((r) => { if (r.parentId) { const a = byParent.get(r.parentId) ?? []; a.push(r); byParent.set(r.parentId, a); } });
    const out: Array<{ id: string; name: string; type: string; depth: number; parentId: string; hasChildren: boolean }> = [];
    const walk = (parentId: string, depth: number) => {
      const kids = (byParent.get(parentId) ?? []).sort((a, b) => a.name.localeCompare(b.name));
      for (const k of kids) {
        if (["uretim_hatti", "unite", "makina"].includes(k.type)) {
          const equipKids = (byParent.get(k.id) ?? []).some((c) => ["uretim_hatti", "unite", "makina"].includes(c.type));
          out.push({ id: k.id, name: k.name, type: k.type, depth, parentId, hasChildren: equipKids });
          walk(k.id, depth + 1);
        }
      }
    };
    walk(centerId, 0);
    return out;
  }, [locsQ.data, centerId]);
  const eqById = useMemo(() => new Map(equipment.map((e) => [e.id, e])), [equipment]);

  // Görünür satırlar: aramada eşleşen + ata zinciri (hiyerarşi okunur kalsın);
  // normalde yalnız üst seviyeler + açılmış dallar (varsayılan hepsi kapalı).
  const trNorm = (s: string) => s.toLocaleLowerCase("tr-TR");
  const eqSearch = eqQuery.trim();
  const visibleEquipment = useMemo(() => {
    if (eqSearch) {
      const q = trNorm(eqSearch);
      const show = new Set<string>();
      for (const e of equipment) {
        if (trNorm(e.name).includes(q)) {
          show.add(e.id);
          let p: string | undefined = e.parentId;
          while (p && p !== centerId) { show.add(p); p = eqById.get(p)?.parentId; }
        }
      }
      return equipment.filter((e) => show.has(e.id));
    }
    return equipment.filter((e) => {
      let p: string | undefined = e.parentId;
      while (p && p !== centerId) {
        if (!expandedEq.has(p)) return false;
        p = eqById.get(p)?.parentId;
      }
      return true;
    });
  }, [equipment, eqById, eqSearch, expandedEq, centerId]);

  function toggleEq(id: string) {
    setExpandedEq((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  // Seçimde ata zinciri açılır — arama temizlenince seçili düğüm görünür kalsın.
  function pickEquip(id: string | null) {
    setLocationId(id);
    if (id) {
      setExpandedEq((s) => {
        const n = new Set(s);
        let p = eqById.get(id)?.parentId;
        while (p && p !== centerId) { n.add(p); p = eqById.get(p)?.parentId; }
        return n;
      });
    }
  }

  const catalog = (catQ.data ?? []) as Array<{ id: string; code: string | null; label: string; defaultSeverity: Severity | null }>;
  const catOptions = useMemo(() => catalog.map((x) => ({ value: x.id, label: x.label, desc: x.code ?? undefined, tail: x.defaultSeverity ? SEV_LABELS[x.defaultSeverity] : undefined })), [catalog]);

  function pickCatalog(v: string | null) {
    setCatalogId(v);
    const c = catalog.find((x) => x.id === v);
    if (c?.defaultSeverity && !severity) setSeverity(c.defaultSeverity);
  }

  const reports = reportsQ.data?.rows ?? [];

  async function save() {
    setErr(null);
    if (!catalogId) { setErr("Arıza tipi seçin (liste boşsa Lokasyonlar > Arıza Kataloğu'ndan tanımlayın)."); return; }
    if (!startedAt) { setErr("Başlangıç zamanı zorunlu."); return; }
    try {
      await createMut.mutateAsync({
        centerId, locationId: locationId ?? null, catalogId,
        description: description.trim() || null, severity: severity || null,
        productionStopped, startedAt: new Date(startedAt).toISOString(),
        durationMin: intOrNull(durationMin), estimatedDurationMin: intOrNull(estMin),
      });
      await qc.invalidateQueries();
      resetForm();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Arıza Bildirimi"
      subtitle={centerName ? `Merkez: ${centerName}` : undefined}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>Kapat</button>
          <button type="button" className="btn-md primary" onClick={save} disabled={createMut.isPending}>
            <Icon name="plus" size={14} />{createMut.isPending ? "Kaydediliyor…" : "Arıza Kaydet"}
          </button>
        </>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      {/* Ekipman ağacı seçimi: arama + varsayılan-kapalı ağaç (chevron ile açılır) */}
      <div className="form-field">
        <label className="lbl" htmlFor="fr-eq-search">Ekipman (arızanın yeri)</label>
        <input
          id="fr-eq-search"
          className="form-input"
          type="text"
          value={eqQuery}
          onChange={(e) => setEqQuery(e.target.value)}
          placeholder="Ekipman ara… (ünite/makina adı)"
          style={{ marginBottom: 6 }}
        />
        <div style={{ border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", maxHeight: 180, overflowY: "auto", padding: 4 }}>
          <EquipRow name="Merkez geneli (ekipman seçme)" type={null} depth={0} selected={locationId === null} onClick={() => setLocationId(null)} />
          {equipment.length === 0 ? (
            <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--neutral-400)" }}>Bu merkeze hat/ünite/makina tanımlı değil — Lokasyonlar'dan ekleyebilirsiniz.</div>
          ) : eqSearch && visibleEquipment.length === 0 ? (
            <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--neutral-400)" }}>"{eqSearch}" ile eşleşen ekipman yok.</div>
          ) : visibleEquipment.map((e) => (
            <EquipRow
              key={e.id}
              name={e.name}
              type={e.type}
              depth={e.depth + 1}
              selected={locationId === e.id}
              onClick={() => pickEquip(e.id)}
              hasChildren={e.hasChildren}
              expanded={!!eqSearch || expandedEq.has(e.id)}
              onToggle={eqSearch ? undefined : () => toggleEq(e.id)}
            />
          ))}
        </div>
      </div>

      {/* Arıza tipi (aranabilir) */}
      <div className="form-field">
        <label className="lbl">Arıza Tipi <span className="req">*</span></label>
        {catalog.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--warning)", padding: "6px 0" }}>Bu merkez için arıza tipi tanımlı değil. Lokasyonlar → merkez → <b>Arıza Kataloğu</b>'ndan ekleyin.</div>
        ) : (
          <SearchSelect value={catalogId} options={catOptions} onChange={pickCatalog} placeholder="Arıza tipi ara/seç…" searchPlaceholder="Ara…" icon="alertTriangle" seedQuery={eqQuery} />
        )}
        {eqSearch && catalog.length > 0 ? (
          <span className="help">Ekipman aramasındaki "{eqSearch}" arıza tipi listesine de uygulandı — açınca değiştirebilirsiniz.</span>
        ) : null}
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="fr-start">Başlangıç <span className="req">*</span></label>
          <input id="fr-start" className="form-input" type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="fr-sev">Önem</label>
          <select id="fr-sev" className="form-select" value={severity} onChange={(e) => setSeverity(e.target.value as Severity | "")}>
            <option value="">—</option>
            {SEV_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="fr-dur">Süre (dk)</label>
          <input id="fr-dur" className="form-input" type="text" inputMode="numeric" value={durationMin} onChange={(e) => setDurationMin(e.target.value)} placeholder="gerçekleşen" />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="fr-est">Tahmini Süre (dk)</label>
          <input id="fr-est" className="form-input" type="text" inputMode="numeric" value={estMin} onChange={(e) => setEstMin(e.target.value)} placeholder="ne kadar sürer" />
        </div>
      </div>

      <div className="form-field">
        <label htmlFor="fr-stop" style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", padding: "8px 10px", border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: productionStopped ? "var(--danger-subtle)" : "var(--surface, #fff)" }}>
          <input id="fr-stop" type="checkbox" checked={productionStopped} onChange={(e) => setProductionStopped(e.target.checked)} />
          <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--neutral-900)" }}>Üretim durdu</span>
          <span style={{ fontSize: 12, color: "var(--neutral-600)" }}>— bu arıza üretimi durdurduysa işaretleyin</span>
        </label>
      </div>

      <div className="form-field">
        <label className="lbl" htmlFor="fr-desc">Açıklama</label>
        <textarea id="fr-desc" className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={1000} placeholder="Arıza detayı…" />
      </div>

      {/* Son bildirimler */}
      <div style={{ borderTop: "1px solid var(--neutral-200)", paddingTop: 14, marginTop: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Son Arıza Bildirimleri ({reportsQ.data?.total ?? 0})</div>
        {reportsQ.isLoading ? (
          <div style={{ padding: 8, color: "var(--neutral-500)", fontSize: 13 }}>Yükleniyor…</div>
        ) : reports.length === 0 ? (
          <div style={{ padding: 8, color: "var(--neutral-500)", fontSize: 13 }}>Henüz arıza bildirimi yok.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {reports.map((r) => (
              <div key={r.id} className="d-card" style={{ padding: "8px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{r.faultLabel}</span>
                  {r.locationName ? <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>· {r.locationType ? `${TYPE_LABELS[r.locationType] ?? ""} ` : ""}{r.locationName}</span> : <span style={{ fontSize: 11.5, color: "var(--neutral-400)" }}>· Merkez geneli</span>}
                  <span style={{ flex: 1 }} />
                  {r.productionStopped ? <span className="badge-pill" style={{ color: "var(--danger)", background: "var(--danger-subtle)" }}>Üretim durdu</span> : null}
                  <span className="badge-pill" style={{ color: r.status === "open" ? "var(--warning)" : "var(--neutral-500)", background: r.status === "open" ? "var(--warning-subtle)" : "var(--neutral-100)" }}>{STATUS_LABELS[r.status] ?? r.status}</span>
                  {r.status === "open" && canMaint ? (
                    <button type="button" className="iact warning" aria-label="Bakım emrine dönüştür" title="Bakım emrine dönüştür" onClick={() => setConvertTarget({ id: r.id, label: r.faultLabel, locationId: r.locationId, severity: r.severity })} disabled={convertMut.isPending}>
                      <Icon name="wrench" size={15} />
                    </button>
                  ) : null}
                  {r.status === "open" ? (
                    <button type="button" className="iact success" aria-label="Çözüldü olarak işaretle" title="Çözüldü olarak işaretle" onClick={() => resolveMut.mutate({ id: r.id })} disabled={resolveMut.isPending}>
                      <Icon name="check" size={15} />
                    </button>
                  ) : null}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--neutral-500)", marginTop: 3, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "var(--font-mono)" }}>{new Date(r.startedAt).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</span>
                  {r.durationMin != null ? <span>{r.durationMin} dk</span> : r.estimatedDurationMin != null ? <span>~{r.estimatedDurationMin} dk (tahmini)</span> : null}
                  {r.severity ? <span>{SEV_LABELS[r.severity as Severity]}</span> : null}
                  {r.reportedBy ? <span>{r.reportedBy}</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
        {convertedCode ? (
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--success)" }}>
            Bakım emri oluşturuldu: <b style={{ fontFamily: "var(--font-mono)" }}>{convertedCode}</b> — Bakım / Onarım sayfasından takip edebilirsiniz.
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={!!convertTarget}
        title="Bakım emrine dönüştür?"
        message={<><b>{convertTarget?.label}</b> arızası için bakım emri açılacak; arıza "Bakımda" durumuna geçer ve bakım tamamlanana kadar hat çalıştırılamaz.</>}
        tone="primary"
        confirmLabel="Dönüştür"
        cancelLabel="Vazgeç"
        busy={convertMut.isPending}
        onConfirm={async () => {
          if (!convertTarget) return;
          try {
            const res = await convertMut.mutateAsync({
              type: "corrective",
              title: `Arıza: ${convertTarget.label}`,
              locationId: convertTarget.locationId,
              priority: (convertTarget.severity as "low" | "medium" | "high" | "critical" | null) ?? null,
              faultReportId: convertTarget.id,
            });
            setConvertedCode(res.code);
            setConvertTarget(null);
            await qc.invalidateQueries();
          } catch (e) { setErr((e as Error).message); setConvertTarget(null); }
        }}
        onCancel={() => setConvertTarget(null)}
      />
    </Drawer>
  );
}

function EquipRow({ name, type, depth, selected, onClick, hasChildren = false, expanded = false, onToggle }: {
  name: string; type: string | null; depth: number; selected: boolean; onClick: () => void;
  hasChildren?: boolean; expanded?: boolean; onToggle?: () => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, paddingLeft: depth * 16 }}>
      {/* Alt dalı olan düğümde aç/kapa chevron'u (aramada gizli — eşleşme zaten açık gösterilir) */}
      {hasChildren && onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? "Daralt" : "Genişlet"}
          aria-expanded={expanded}
          style={{
            width: 18, height: 18, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
            border: "none", background: "transparent", borderRadius: 4, cursor: "pointer", color: "var(--neutral-500)", padding: 0,
            transform: expanded ? "rotate(90deg)" : "none", transition: "transform 100ms",
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
      ) : (
        <span style={{ width: 18, flexShrink: 0 }} />
      )}
      <button
        type="button"
        onClick={onClick}
        style={{
          display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, textAlign: "left",
          padding: "5px 8px", border: "none", borderRadius: 6, cursor: "pointer",
          background: selected ? "var(--primary-subtle)" : "transparent", color: "var(--neutral-800)", fontSize: 13,
        }}
      >
        <span style={{ width: 12, display: "inline-flex", justifyContent: "center", color: selected ? "var(--primary)" : "var(--neutral-300)" }}>
          {selected ? <Icon name="check" size={13} /> : <span style={{ width: 7, height: 7, borderRadius: 999, border: "1.5px solid currentColor", display: "inline-block" }} />}
        </span>
        {type ? <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", padding: "1px 5px", borderRadius: 4, color: "var(--neutral-600)", background: "var(--neutral-100)", whiteSpace: "nowrap" }}>{TYPE_LABELS[type] ?? type}</span> : null}
        <span style={{ fontWeight: selected ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      </button>
    </div>
  );
}
